/* eslint-disable react/prop-types */
import { DeleteOutline, Download } from '@mui/icons-material'
import { Box, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { memo, useCallback, useEffect, useState } from 'react'
import { MAX_IMAGE_SIZE_BYTES, MAX_IMAGE_SIZE_MB, getDownloadUrl, getFilenameFromUrl } from '../../Products/components/Dropzone'
import { useDropzone } from 'react-dropzone'

const AddFoto: React.FC<any> = (props: any) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { foto, setFoto, setDropError, gif, medidas } = props
  const [imgError, setImgError] = useState(false)
  const [imgLoaded, setImgLoaded] = useState(false)

  useEffect(() => {
    setImgError(false)
    setImgLoaded(false)
  }, [foto])

  const onDrop = useCallback(async (acceptedFiles: File[], rejectedFiles: any[]) => {
    setDropError(undefined)

    if (rejectedFiles && rejectedFiles.length > 0) {
      rejectedFiles.forEach((file: any) => {
        file.errors.forEach((err: any) => {
          if (err.code === 'file-invalid-type') setDropError('Solamente podés subir archivos JPG, JPEG o PNG')
          else setDropError('Error desconocido')
        })
      })
      return
    }

    const file = acceptedFiles[0]
    if (!file) return
    props.setFilename && props.setFilename(file.name.replace(/\.[^/.]+$/, ''))

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setDropError(`El tamaño máximo de la imagen es de ${MAX_IMAGE_SIZE_MB}MB`)
      return
    }

    if (medidas) {
      const imageBitmap = await createImageBitmap(file)
      const [requiredWidth, requiredHeight] = medidas.split('x').map(Number)
      if (imageBitmap.height !== requiredHeight || imageBitmap.width !== requiredWidth) {
        setDropError(
          `La imagen debe tener dimensiones de ${requiredWidth}x${requiredHeight}px. ` +
            `Tu imagen tiene ${imageBitmap.width}x${imageBitmap.height}px.`
        )
        return
      }
    }

    const reader = new FileReader()
    reader.onloadend = () => setFoto(reader.result as string)
    reader.readAsDataURL(file)
  }, [])

  const handleDownload = useCallback(async (src: string) => {
    try {
      if (src.startsWith('data:')) {
        const link = document.createElement('a')
        link.href = src
        link.download = 'imagen.jpg'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        return
      }
      const response = await fetch(getDownloadUrl(src))
      if (!response.ok) throw new Error('Respuesta no válida al descargar la imagen')
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = getFilenameFromUrl(src)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.log(error)
      // Si el bucket no devuelve CORS para este origen, fetch() falla: se la pedimos directo al navegador
      // (en cross-origin el atributo download se ignora y la imagen se abre en otra pestaña)
      const link = document.createElement('a')
      link.href = src
      link.download = getFilenameFromUrl(src)
      link.target = '_blank'
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }, [])

  const {
    getRootProps,
    getInputProps,
    open: openFiles,
    isDragActive
  } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    accept: {
      'image/jpeg': [],
      'image/png': [],
      'image/jpg': [],
      ...(gif && { 'image/gif': [] })
    },
    maxFiles: 1
  })

  return (
    <>
      {/* Dropzone */}
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed #aaa',
          borderRadius: 2,
          p: foto ? 0 : 3,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: isDragActive ? 'action.hover' : 'transparent',
          display: 'inline-block'
        }}
      >
        <input {...getInputProps()} />

        {!foto ? (
          <>
            {isMobile ? (
              <Typography onClick={() => openFiles()}>Tocá para seleccionar</Typography>
            ) : (
              <Typography onClick={() => openFiles()}>
                {isDragActive ? 'Soltá la foto aquí...' : 'Arrastrá una foto o hacé click para seleccionar'}
              </Typography>
            )}
          </>
        ) : imgError ? (
          <Typography color="text.secondary" onClick={() => openFiles()} sx={{ px: 2, py: 1 }}>
            No se pudo cargar la imagen. {isMobile ? 'Tocá' : 'Hacé click'} para seleccionar otra
          </Typography>
        ) : (
          <>
            <Box sx={{ position: 'relative', display: 'inline-block', width: '100%' }}>
              <img
                src={foto}
                alt="foto"
                onLoad={() => setImgLoaded(true)}
                onError={() => setImgError(true)}
                style={{
                  maxWidth: '100%',
                  display: 'block',
                  borderRadius: '8px'
                }}
              />
              {imgLoaded && isMobile && (
                <IconButton
                  size="small"
                  onClick={() => handleDownload(foto)}
                  sx={{
                    position: 'absolute',
                    top: 44,
                    right: 8,
                    bgcolor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                  }}
                >
                  <Download fontSize="small" />
                </IconButton>
              )}
              {/* Botón para eliminar la imagen */}
              {imgLoaded && (
                <IconButton
                  size={isMobile ? 'small' : 'medium'}
                  onClick={() => setFoto(null)}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                  }}
                >
                  <DeleteOutline fontSize={isMobile ? 'small' : 'medium'} />
                </IconButton>
              )}
            </Box>
          </>
        )}
      </Box>
    </>
  )
}

export default memo(AddFoto)
